import React, {useContext, useEffect, useState} from "react";
import {Splide, SplideSlide} from "@splidejs/react-splide";
import {FiCopy, FiTrash2} from "react-icons/fi";
import {Accordion, Toggle} from "../../../../components";
import {useCurrentUser} from "../../../../contexts/UserContext";
import {FOOT, INCH, METER, UnitContext} from "../../../../contexts/UnitContext";
import {Alert, Button, Flex, Select} from "antd";
import {useViewerStore} from "../../../../store/store";
import {ArrowsOutCardinal, Plus, SelectionAll} from "@phosphor-icons/react";
import SliderInput from "../../../../components/Pure-UI-Components/SliderInput/SliderInput";
import {SHADE_CALCULATIONS, siteCalculationState} from "../../../../services/siteCalculationState";
import {GetRoundNum} from "../../../../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/Common";
import {SLIDER_PROPERTIES_SETTINGS, treeList} from "../../../../Constants";
import TotalSystemEfficiency from 
    "../../../../components/Layout-Components/Editor-Layout/Popout-Window/Production-Calculation-Popout/Production-Calculation-Popout-Tabs/TotalSystemEfficiency";
import RoofInformationTable from 
    "../../../../components/Layout-Components/Editor-Layout/Popout-Window/Production-Calculation-Popout/Production-Calculation-Popout-Tabs/RoofInformationTable";
import {removeSelectedTree, registerRef} from "../../../../helpers/storeHelpers";
import { TreeData, TreeGroup } from "../../../../@types/types";
import { AlertType, HideObjectType, MeshType, SelectType, EditAction, TreeProperty } from "../../../../@types/enums";
import * as THREE from 'three';

const TreeProperties: React.FC = () => {
    const {hasCapability} = useCurrentUser();
    const {treeUnit, convertQuantityToUnit, convertTypeQuantityToUnit, renderUnit} = useContext(UnitContext);
    const selectedTrees = useViewerStore(state => state.selectedTrees);
    const toggleTreeLabel = () => useViewerStore.setState({isTreeLabelVisible: !isTreeLabelVisible});
    const toggleTreeHeightLabel = () => useViewerStore.setState({isTreeHeightLabelVisible: !isTreeHeightLabelVisible});
    const layerVisibilityState =  useViewerStore(state => state.layerVisibilityState);
    const isTreeMultiplierLabelVisible = useViewerStore((state) => state.isTreeMultiplierLabelVisible);
    const selectedTree = selectedTrees[0];

    const [height, setHeight] = useState(GetRoundNum(convertTypeQuantityToUnit(MeshType.Tree, selectedTree?.userData.height ?? 0, METER), 2));
    const [crownDiameter, setCrownDiameter] = useState(GetRoundNum(convertTypeQuantityToUnit(MeshType.Tree, selectedTree?.userData.crown_radius ?? 0, METER), 2));
    const [crownHeight, setCrownHeight] = useState(GetRoundNum(convertTypeQuantityToUnit(MeshType.Tree, selectedTree?.userData.crown_height ?? 0, METER), 2));
    const [trunkRadius, setTrunkRadius] = useState(GetRoundNum(convertTypeQuantityToUnit(MeshType.Tree, selectedTree?.userData.trunk_radius ?? 0, METER), 2));

    const { sceneViewer, isHideAllGhostTrees, isHideAllSelectedTrees, isTreePerimeterToolActive, 
        isAddTreeActive, isMoveTreeActive, hasTreeMoved, isTreeLabelVisible, isTreeHeightLabelVisible } = useViewerStore();

    useEffect(() => {
        setHeight(GetRoundNum(convertTypeQuantityToUnit(MeshType.Tree, selectedTree?.userData.height ?? 0, METER), 2));
        setCrownDiameter(GetRoundNum(convertTypeQuantityToUnit(MeshType.Tree, (selectedTree?.userData.crown_radius ?? 0), METER), 2));
        setCrownHeight(GetRoundNum(convertTypeQuantityToUnit(MeshType.Tree, selectedTree?.userData.crown_height ?? 0, METER), 2));
        setTrunkRadius(GetRoundNum(convertTypeQuantityToUnit(MeshType.Tree, selectedTree?.userData.trunk_radius ?? 0, METER), 2));
    }, [selectedTrees, treeUnit]);

    const changeHeight = (value: number) => {
        sceneViewer?.setTreeProperty(TreeProperty.height, convertQuantityToUnit(value, treeUnit, METER));
        setHeight(value);
    };

    const changeCrownDiameter = (value: number) => {
        sceneViewer?.setTreeProperty(TreeProperty.crown_radius, convertQuantityToUnit(value, treeUnit, METER));
        setCrownDiameter(value);
    };

    const changeCrownHeight = (value: number) => {
        sceneViewer?.setTreeProperty(TreeProperty.crown_height, convertQuantityToUnit(value, treeUnit, METER));
        setCrownHeight(value);
    };

    const changeTrunkRadius = (value: number) => {
        sceneViewer?.setTreeProperty(TreeProperty.trunk_radius, convertQuantityToUnit(value, treeUnit, METER));
        setTrunkRadius(value);
    };

    const toggleTreeNameLabel = () => {
        if(isTreeHeightLabelVisible) toggleTreeNameHeightLabel(); //Disable tree height tag if visible
        if(isTreeMultiplierLabelVisible) toggleTreeMultiplierVisibility(); //Disable tree multiplier if visible
        toggleTreeLabel(); //Updates store
        sceneViewer.toggleTreeLabel(); //Updates in view
    }

    const toggleTreeNameHeightLabel = () => {
        if(isTreeLabelVisible) toggleTreeNameLabel(); //Disable tree label tag if visible
        if(isTreeMultiplierLabelVisible) toggleTreeMultiplierVisibility(); //Disable tree multiplier if visible
        toggleTreeHeightLabel(); //Updates store
        sceneViewer.toggleTreeLabel(false, true); //Updates in view
    }

    const toggleTreeMultiplierVisibility = () => {
        if(isTreeHeightLabelVisible) toggleTreeNameHeightLabel(); //Disable tree height tag if visible
        if(isTreeLabelVisible) toggleTreeNameLabel(); //Disable tree label tag if visible
        sceneViewer.toggleTreeMultiplierLabel(!isTreeMultiplierLabelVisible);
        useViewerStore.setState({isTreeMultiplierLabelVisible: !isTreeMultiplierLabelVisible});
    }

    const onSliderChangeComplete = (selectType: EditAction, newValue: number, oldValue: number) => {

        if (selectedTrees.length === 0) return;
        const selObj: any[] = [];
        selectedTrees.forEach(group => {
            selObj.push(group);
        });
        const originalValue = convertQuantityToUnit(oldValue, treeUnit, METER);
        const newValueMeter = convertQuantityToUnit(newValue, treeUnit, METER);
        sceneViewer.addStep(selectType, selObj, originalValue, newValueMeter);

        siteCalculationState.needsUpdate(SHADE_CALCULATIONS);
        sceneViewer.refreshShade();
    }

    let settingsTotalHeight;
    let settingsCrownHeight;
    let settingsCrownDiameter;
    let settingsTrunkRadius;

    switch (treeUnit) {
        case INCH:
            settingsTotalHeight = SLIDER_PROPERTIES_SETTINGS.TREE_PROPERTIES.HEIGHT.INCH;
            settingsCrownHeight = SLIDER_PROPERTIES_SETTINGS.TREE_PROPERTIES.CROWN_HEIGHT.INCH;
            settingsCrownDiameter = SLIDER_PROPERTIES_SETTINGS.TREE_PROPERTIES.CROWN_DIAMETER.INCH;
            settingsTrunkRadius = SLIDER_PROPERTIES_SETTINGS.TREE_PROPERTIES.TRUNK_RADIUS.INCH;
            break;
        case FOOT:
            settingsTotalHeight = SLIDER_PROPERTIES_SETTINGS.TREE_PROPERTIES.HEIGHT.FOOT;
            settingsCrownHeight = SLIDER_PROPERTIES_SETTINGS.TREE_PROPERTIES.CROWN_HEIGHT.FOOT;
            settingsCrownDiameter = SLIDER_PROPERTIES_SETTINGS.TREE_PROPERTIES.CROWN_DIAMETER.FOOT;
            settingsTrunkRadius = SLIDER_PROPERTIES_SETTINGS.TREE_PROPERTIES.TRUNK_RADIUS.FOOT;
            break;
        case METER:
            settingsTotalHeight = SLIDER_PROPERTIES_SETTINGS.TREE_PROPERTIES.HEIGHT.METER;
            settingsCrownHeight = SLIDER_PROPERTIES_SETTINGS.TREE_PROPERTIES.CROWN_HEIGHT.METER;
            settingsCrownDiameter = SLIDER_PROPERTIES_SETTINGS.TREE_PROPERTIES.CROWN_DIAMETER.METER;
            settingsTrunkRadius = SLIDER_PROPERTIES_SETTINGS.TREE_PROPERTIES.TRUNK_RADIUS.METER;
            break;
    }

    const deselectAllTrees = () => {
        sceneViewer.treeGroup.children.forEach((tree: any) => {
            tree.select = false;
            removeSelectedTree(tree);

            const mesh = tree.children[0];
            if (mesh instanceof THREE.Mesh && mesh.material instanceof THREE.MeshStandardMaterial) {
                mesh.material.emissive.setHex(0x000000);
            }
        });
    };

    useEffect(() => {
        if (isAddTreeActive === false) {
            deselectAllTrees();
        }
    }, [isAddTreeActive]);

    useEffect(() => {
        if (!isMoveTreeActive) {
            deselectAllTrees();
            if (hasTreeMoved) {
                useViewerStore.setState({ hasTreeMoved: false });
                siteCalculationState.needsUpdate(SHADE_CALCULATIONS);
                siteCalculationState.useCalculations(SHADE_CALCULATIONS);
            }
        }
    }, [isMoveTreeActive]);

    useEffect(() => {
        if (!sceneViewer.moveMode && isMoveTreeActive) {
            useViewerStore.setState({ isMoveTreeActive: false });
        }
    }, [sceneViewer.moveMode])

    const selectMethod = (method: string) => {
        sceneViewer.selectType = SelectType.tree;

        if (method === "add") {
            //Remove Move mode
            useViewerStore.setState({isMoveTreeActive: false});
            sceneViewer.setMoveMode(false)

            //Enable Add mode
            useViewerStore.setState({isAddTreeActive: !isAddTreeActive});
            sceneViewer.setPushMode(!isAddTreeActive)
        } else if (method === "move") {

            const selTrees = useViewerStore.getState().selectedTrees;

            if (!selTrees.length) {
                sceneViewer.viewerAlert({
                    show: true,
                    title: "Please select trees to move",
                    messageType: AlertType.warning,
                    isCloseAfterDisplay: true,
                    autoCloseSeconds: 2000,
                });
                return
            }

            //Remove Add mode
            useViewerStore.setState({isAddTreeActive: false});
            sceneViewer.setPushMode(false)

            //Enable Move mode
            useViewerStore.setState({isMoveTreeActive: !isMoveTreeActive});
            sceneViewer.setMoveMode(!isMoveTreeActive)
        }
    };

    const setDeleteTree = (e: React.MouseEvent<SVGElement>) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains("disabled")) return;
        if (!sceneViewer.selTreeArr?.length) {
            sceneViewer.viewerAlert({
                show: true,
                title: "Please select trees to delete",
                messageType: AlertType.warning,
                isCloseAfterDisplay: true,
                autoCloseSeconds: 2000,
            });
            return;
        }
        const olderValueArr = sceneViewer.selTreeArr;
        sceneViewer.setTreeProperty(TreeProperty.delete);
        sceneViewer.addStep( EditAction.GhostTree, '', olderValueArr, sceneViewer.selTreeArr);
    };

    const onClickSelectAll = (e: React.MouseEvent<HTMLElement>) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains("disabled")) return;
        sceneViewer.selectAllTrees();
    };

    const onChangeTreeType = (e: React.MouseEvent<HTMLElement>, item: TreeData) => {
        //add active class to selected tree
        const treeList = document.getElementsByClassName("carousel__img");
        for (let i = 0; i < treeList.length; i++) {
            treeList[i].classList.remove("active");
        }
        const target = e.target as HTMLElement;
        target.parentElement?.classList.add("active");
        /*********** Tree type change begin ********/
        const selObj: TreeGroup[] = [], originalValueArr: string[] = [], newValueArr: string[] = [];
        selectedTrees.forEach(group => {
            selObj.push(group);
            originalValueArr.push(group?.children[0]?.userData?.crownKey);
            newValueArr.push(item.cM);
        });
        sceneViewer.addStep( EditAction.TreeShape, selObj, originalValueArr, newValueArr);
        /*********** Tree type change end *********/

        sceneViewer.setTreeProperty(TreeProperty.shape, item.cM);
    };

    const onClickCopy = () => {
        sceneViewer.setPushMode(true, true);
        useViewerStore.setState({isAddTreeActive: true});
    };


    const antDCompatibleOptions = [
        {label: '150 ft', value: convertQuantityToUnit(150, FOOT, METER)},
        {label: '200 ft', value: convertQuantityToUnit(200, FOOT, METER)},
        {label: '250 ft', value: convertQuantityToUnit(250, FOOT, METER)},
    ]


    const roofSquirrelHandler = () => {
        sceneViewer.calculateRoofSquirrel()
    }

    const roofSquirrelRadiusHandler = (value: number) => {
        sceneViewer.updateRoofSquirrelRadius(value)
    }

    const onChangeHideAllGhostTrees = () => {
        sceneViewer.hideObject(HideObjectType.GhostTree, !isHideAllGhostTrees);
        useViewerStore.setState({isHideAllGhostTrees: !isHideAllGhostTrees});
    }

    const onChangeHideSelectedTrees = () => {
        sceneViewer.hideObject(HideObjectType.Seltree, !isHideAllSelectedTrees);
        useViewerStore.setState({isHideAllSelectedTrees: !isHideAllSelectedTrees});
    }

    const onChangeTreePerimeterTool = () => {
        useViewerStore.setState({isTreePerimeterToolActive: !isTreePerimeterToolActive});
        sceneViewer.showRoofSquirrel(!isTreePerimeterToolActive);
        document.getElementById('roofSquirrelCalculateContainer')?.classList.toggle('disabled');
    }


    const tree_Type = (
        <div className="tab__column">
            <Accordion title='Tree Type' active={hasCapability('Edit_Tree') ? false : true}>
            <div style={{paddingLeft: '15px', paddingRight: '15px', marginBottom: '0px', paddingBottom: '0px'}}>
                <Splide
                    options={{
                        rewind: false,
                        width: "100%",
                        gap: "10px",
                        perPage: 3,
                        perMove: 1,
                    }}
                >
                    {treeList.map((item) => {
                        return (
                            <SplideSlide key={item.id}>
                                <Button
                                    type='text'
                                    className='carousel__img'
                                    onClick={(e) => onChangeTreeType(e, item)}
                                >
                                    <img src={item.img} alt={item.alt} title={item.alt}/>
                                </Button>
                            </SplideSlide>
                        );
                    })}
                </Splide>
                </div>
            </Accordion>
        </div>
    );

    const system_Info = (
        <div className='tab__column'>
            <Accordion title='Show/Hide System Information' active={false}>
                <div className='tab__column' style={{paddingTop: '0px', paddingLeft: '8px', paddingRight: '8px'}}>
                    <span className='tab__column--title' style={{marginBottom: '0px'}}>Total System Efficiency</span>
                    <TotalSystemEfficiency popoutVersion={false}/>
                    <span className='tab__column--title' style={{position: "relative", marginBottom: '2px'}}>Roof Information Table</span>
                    <RoofInformationTable shortened={true}/>
                </div> 
            </Accordion>
        </div>  
    )

    const tree_Properties = (
        <div className= {`tab__column tree-edit-mask ${!selectedTree && 'disabled'}`}>
            <span className="tab__title--text">Tree Properties</span>
            <span className="tab__column--subtitle">Height ({treeUnit})</span>

            <SliderInput
                settings={settingsTotalHeight}
                value={height}
                unit={renderUnit(treeUnit)}
                onChange={(value: number) => changeHeight(value)}
                onChangeComplete={(newVal, oldVal) => onSliderChangeComplete(EditAction.TreeHeight, newVal, oldVal)}
            />

            <span className="tab__column--subtitle">Crown Diameter ({treeUnit})</span>
            <SliderInput
                settings={settingsCrownDiameter}
                value={crownDiameter}
                unit={renderUnit(treeUnit)}
                onChange={changeCrownDiameter}
                onChangeComplete={(newVal, oldVal) => onSliderChangeComplete(EditAction.TreeCrownDiameter, newVal, oldVal)}
            />

            <span className="tab__column--subtitle">Crown Height ({treeUnit})</span>
            <SliderInput
                settings={settingsCrownHeight}
                value={crownHeight}
                unit={renderUnit(treeUnit)}
                onChange={changeCrownHeight}
                onChangeComplete={(newVal, oldVal) => onSliderChangeComplete(EditAction.TreeCrownHeight, newVal, oldVal)}
            />

            <span className="tab__column--subtitle">Trunk Radius ({treeUnit})</span>
            <SliderInput
                settings={settingsTrunkRadius}
                value={trunkRadius}
                unit={renderUnit(treeUnit)}
                onChange={changeTrunkRadius}
                onChangeComplete={(newVal, oldVal) => onSliderChangeComplete(EditAction.TreeTrunkRadius, newVal, oldVal)}
            />

        </div>
    );

    const toggleTree = (
        <>
            <div className="tab__column tree-edit-mask">
                <Button
                    icon={<SelectionAll size={18}/>}
                    id='selAllBtn'
                    type='default'
                    size='middle'
                    className='btnSelectAllTrees'
                    style={{justifySelf: 'center'}}
                    onClick={(e) => onClickSelectAll(e)}
                >
                    Select All Trees
                </Button>
                <span className="tab__spacer"></span>

                {
                    hasCapability('Toggle_Tree') &&
                    <>
                        <div className={"tab__flex justify-between mb-1"}>
                            <span className="tab__column--subtitle mb-0">Hide All Ghost Trees</span>
                            <Toggle
                                on={isHideAllGhostTrees}
                                onChange={onChangeHideAllGhostTrees}
                            />
                        </div>
                        <div className='tab__flex justify-between mb-1'>
                            <span className='tab__column--subtitle mb-0'>Show Tree Multiplier</span>
                            <Toggle
                                onChange={toggleTreeMultiplierVisibility}
                                property='TreeMultiplier'
                                on={isTreeMultiplierLabelVisible}
                                disabled={!layerVisibilityState.tree}
                            />
                        </div>
                        <div className='tab__flex justify-between mb-1'>
                            <span className='tab__column--subtitle mb-0'>Show Tree Label</span>
                            <Toggle
                                on={isTreeLabelVisible}
                                onChange={toggleTreeNameLabel}
                                property='treeNameLabel'>
                            </Toggle>
                        </div>
                        <div className='tab__flex justify-between mb-1'>
                            <span className='tab__column--subtitle mb-0'>Show Tree Height</span>
                            <Toggle
                                on={isTreeHeightLabelVisible}
                                onChange={toggleTreeNameHeightLabel}
                                property='treeNameLabel'>
                            </Toggle>
                        </div>
                    </>

                }


            </div>


            <div className='tab__column'>
                <div className='tab__flex justify-between'>
                    <span className='tab__column--subtitle mb-0'>Tree Perimeter Tool</span>
                    <Toggle
                        on={isTreePerimeterToolActive}
                        onChange={onChangeTreePerimeterTool}
                    />
                </div>


                <div id={"roofSquirrelCalculateContainer"} className='tab__column disabled'>
                    <div className='tab__flex hidden'>
                        <Select
                            options={antDCompatibleOptions}
                            onChange={(value) => roofSquirrelRadiusHandler(value)}
                            defaultValue={antDCompatibleOptions[0].value}
                        />


                        <Button
                            title={'Calculates number of trees inside the selected perimeter'}
                            onClick={roofSquirrelHandler}
                            size='middle'
                        >
                            Calculate
                        </Button>
                    </div>
                </div>

            </div>

        </>
    );


    return (
        <>
            <div className="tab__inner">
                {/* DELETE */}
                <div className="tab__title">
                    <span className="tab__title--text">Tree</span>
                    <div className='tab__title--btns'>
                    {
                        hasCapability('Edit_Tree') && hasCapability('Edit_Tree_Remove') && 
                        <FiCopy onClick={onClickCopy}/>
                    }
                    {
                        hasCapability('Toggle Ghost Tree') &&<FiTrash2 onClick={setDeleteTree}/>
                    }
                    </div>
                </div>


                {/* ADD, EDIT OR MOVE TREES */}
                {
                    hasCapability('Edit_Tree') &&
                    <div className="tab__column">
                        <Flex align='center' justify='center' gap={15}>
                            {hasCapability('Edit_Tree_Add') &&
                                <Button
                                    ref={registerRef('btnTreeAdd')}
                                    icon={<Plus size={18}/>}
                                    className={`${isAddTreeActive ? 'active' : ''}`}
                                    id='btnTreeAdd'
                                    onClick={() => selectMethod("add")}
                                >
                                    Add
                                </Button>}
                            {hasCapability('Edit_Tree_Move') &&
                                <Button
                                    ref={registerRef('moveTreeModeBtn')}
                                    icon={<ArrowsOutCardinal size={18}/>}
                                    className={`${isMoveTreeActive ? 'active' : ''}`}
                                    id='moveTreeModeBtn'
                                    onClick={() => selectMethod("move")}
                                >
                                    Move
                                </Button>}
                        </Flex>
                    </div>
                }

                {/* TREE TYPE, SYSTEM INFO, and SIZE*/}
                {hasCapability('Edit_Tree') && hasCapability('Edit_Tree_Modify_Size') ? (
                    <>
                        {system_Info}
                        {tree_Type}
                        {tree_Properties}
                    </>
                ) : (
                    <>
                        {system_Info}
                        <div className="tab__column disabled" style={{padding: '0px'}}>
                            {tree_Properties}
                        </div>
                    </>
                )}

                {/* TOGGLE GHOST AND SELECTED TREES, TREE PERIMETER TOOL */}
                {
                    <>{toggleTree}</>
                }
            </div>
        </>
    );
};

export default TreeProperties;
